import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { APP_ROUTES, HOME, canAccess } from "@/lib/routes";
import { ComponentShowcase } from "@/pages/ComponentShowcase";
import { ForbiddenPage, NotFoundPage } from "@/pages/NotFound";
import { EVENT_TABS } from "@/features/events/tabs";
import { PlaceholderPage } from "@/pages/PlaceholderPage";
import { lazy, Suspense, type ReactElement } from "react";

// Screens load on demand so the sign-in page and shell stay small.
const LoginPage = lazy(() =>
  import("@/features/auth/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const AppShell = lazy(() =>
  import("@/features/shell/AppShell").then((m) => ({ default: m.AppShell })),
);
const UsersPage = lazy(() =>
  import("@/features/users/UsersPage").then((m) => ({ default: m.UsersPage })),
);

const ImportPreviewPage = lazy(() =>
  import("@/features/imports/ImportPreviewPage").then((m) => ({ default: m.ImportPreviewPage })),
);
const EventsPage = lazy(() =>
  import("@/features/events/EventsPage").then((m) => ({ default: m.EventsPage })),
);
const EventWorkspaceLayout = lazy(() =>
  import("@/features/events/EventWorkspaceLayout").then((m) => ({
    default: m.EventWorkspaceLayout,
  })),
);
const NeedsMePage = lazy(() =>
  import("@/features/queues/NeedsMePage").then((m) => ({ default: m.NeedsMePage })),
);
const ApprovalsPage = lazy(() =>
  import("@/features/approvals/ApprovalsPage").then((m) => ({ default: m.ApprovalsPage })),
);
const WaitingOnHostPage = lazy(() =>
  import("@/features/events/WaitingPages").then((m) => ({ default: m.WaitingOnHostPage })),
);
const WaitingOnVendorsPage = lazy(() =>
  import("@/features/events/WaitingPages").then((m) => ({ default: m.WaitingOnVendorsPage })),
);
const HostsPage = lazy(() =>
  import("@/features/hosts/HostsPage").then((m) => ({ default: m.HostsPage })),
);
const HostDetailPage = lazy(() =>
  import("@/features/hosts/HostDetailPage").then((m) => ({ default: m.HostDetailPage })),
);
const InboxListPage = lazy(() =>
  import("@/features/inbox/InboxListPage").then((m) => ({ default: m.InboxListPage })),
);
const InboxThreadPage = lazy(() =>
  import("@/features/inbox/InboxThreadPage").then((m) => ({ default: m.InboxThreadPage })),
);
// Ajv and the diff library load with this chunk only.
const ConfigPage = lazy(() =>
  import("@/features/config/ConfigPage").then((m) => ({ default: m.ConfigPage })),
);
const ConfigKeyPage = lazy(() =>
  import("@/features/config/ConfigKeyPage").then((m) => ({ default: m.ConfigKeyPage })),
);
const EventTabPlaceholder = lazy(() =>
  import("@/features/events/EventTabPlaceholder").then((m) => ({
    default: m.EventTabPlaceholder,
  })),
);
const NotificationsPage = lazy(() =>
  import("@/features/notifications/NotificationsPage").then((m) => ({
    default: m.NotificationsPage,
  })),
);
const IntakePage = lazy(() =>
  import("@/features/intake/IntakePage").then((m) => ({ default: m.IntakePage })),
);
const ProblemsPage = lazy(() =>
  import("@/features/problems/ProblemsPage").then((m) => ({ default: m.ProblemsPage })),
);
const FailuresPage = lazy(() =>
  import("@/features/operations/FailuresPage").then((m) => ({ default: m.FailuresPage })),
);
const VendorsPage = lazy(() =>
  import("@/features/vendors/VendorsPage").then((m) => ({ default: m.VendorsPage })),
);
const VendorDetailPage = lazy(() =>
  import("@/features/vendors/VendorDetailPage").then((m) => ({ default: m.VendorDetailPage })),
);
const BeoPage = lazy(() =>
  import("@/features/packages/BeoPage").then((m) => ({ default: m.BeoPage })),
);
const StaffPackagePreview = lazy(() =>
  import("@/features/packages/StaffPackagePreview").then((m) => ({
    default: m.StaffPackagePreview,
  })),
);
// The two public pages. Lazy so a host on a phone downloads only the page they were emailed.
const AcceptPage = lazy(() =>
  import("@/features/public/AcceptPage").then((m) => ({ default: m.AcceptPage })),
);
const RatingsPage = lazy(() =>
  import("@/features/ratings/RatingsPage").then((m) => ({ default: m.RatingsPage })),
);
const InterestPage = lazy(() =>
  import("@/features/interest/InterestPage").then((m) => ({ default: m.InterestPage })),
);
const OperationsPage = lazy(() =>
  import("@/features/operations/OperationsPage").then((m) => ({ default: m.OperationsPage })),
);
const TrainingPage = lazy(() =>
  import("@/features/training/TrainingPage").then((m) => ({ default: m.TrainingPage })),
);
const MoneyPage = lazy(() =>
  import("@/features/money/MoneyPage").then((m) => ({ default: m.MoneyPage })),
);
const PublicFeedbackPage = lazy(() =>
  import("@/features/public/FeedbackPage").then((m) => ({ default: m.FeedbackPage })),
);
const PublicTrackerPage = lazy(() =>
  import("@/features/public/TrackerPage").then((m) => ({ default: m.TrackerPage })),
);
const PublicBalancePage = lazy(() =>
  import("@/features/public/BalancePage").then((m) => ({ default: m.BalancePage })),
);

function Loading() {
  return (
    <div role="status" aria-label="Loading" className="text-muted-foreground p-6 text-sm">
      Loading…
    </div>
  );
}

/** Screens that exist. Anything in APP_ROUTES without an entry here shows the placeholder. */
export const SCREENS: Record<string, ReactElement> = {
  "/queues/needs-me": (
    <Suspense fallback={<Loading />}>
      <NeedsMePage />
    </Suspense>
  ),
  "/queues/waiting-on-host": (
    <Suspense fallback={<Loading />}>
      <WaitingOnHostPage />
    </Suspense>
  ),
  "/queues/waiting-on-vendors": (
    <Suspense fallback={<Loading />}>
      <WaitingOnVendorsPage />
    </Suspense>
  ),
  "/approvals": (
    <Suspense fallback={<Loading />}>
      <ApprovalsPage />
    </Suspense>
  ),
  "/events": (
    <Suspense fallback={<Loading />}>
      <EventsPage />
    </Suspense>
  ),
  "/hosts": (
    <Suspense fallback={<Loading />}>
      <HostsPage />
    </Suspense>
  ),
  "/inbox": (
    <Suspense fallback={<Loading />}>
      <InboxListPage />
    </Suspense>
  ),
  "/config": (
    <Suspense fallback={<Loading />}>
      <ConfigPage />
    </Suspense>
  ),
  "/admin/users": (
    <Suspense fallback={<Loading />}>
      <UsersPage />
    </Suspense>
  ),
  "/notifications": (
    <Suspense fallback={<Loading />}>
      <NotificationsPage />
    </Suspense>
  ),
  "/intake": (
    <Suspense fallback={<Loading />}>
      <IntakePage />
    </Suspense>
  ),
  "/queues/problems": (
    <Suspense fallback={<Loading />}>
      <ProblemsPage />
    </Suspense>
  ),
  "/operations/failures": (
    <Suspense fallback={<Loading />}>
      <FailuresPage />
    </Suspense>
  ),
  "/vendors": (
    <Suspense fallback={<Loading />}>
      <VendorsPage />
    </Suspense>
  ),
  "/queues/money": (
    <Suspense fallback={<Loading />}>
      <MoneyPage />
    </Suspense>
  ),
  "/ratings": (
    <Suspense fallback={<Loading />}>
      <RatingsPage />
    </Suspense>
  ),
  "/interest": (
    <Suspense fallback={<Loading />}>
      <InterestPage />
    </Suspense>
  ),
  "/operations": (
    <Suspense fallback={<Loading />}>
      <OperationsPage />
    </Suspense>
  ),
  "/training": (
    <Suspense fallback={<Loading />}>
      <TrainingPage />
    </Suspense>
  ),
  // S23, S24 and S25 have the routes their specs name; each opens the matching tab of the one
  // Money screen, keeping the rest of the query so a `?payout=` deep link still finds its row.
  "/payouts": <MoneyTabRedirect tab="payouts" />,
  "/refunds": <MoneyTabRedirect tab="refunds" />,
  "/disputes": <MoneyTabRedirect tab="disputes" />,
};

function MoneyTabRedirect({ tab }: { tab: string }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set("tab", tab);
  return <Navigate to={`/queues/money?${params}`} replace />;
}

const BriefTab = lazy(() =>
  import("@/features/brief/BriefTab").then((m) => ({ default: m.BriefTab })),
);
const ActivityTab = lazy(() =>
  import("@/features/activity/ActivityTab").then((m) => ({ default: m.ActivityTab })),
);
const ConversationTab = lazy(() =>
  import("@/features/conversation/ConversationTab").then((m) => ({ default: m.ConversationTab })),
);
const ShortlistTab = lazy(() =>
  import("@/features/rfqs/ShortlistTab").then((m) => ({ default: m.ShortlistTab })),
);
const MoneyTab = lazy(() =>
  import("@/features/money/MoneyTab").then((m) => ({ default: m.MoneyTab })),
);
const PackageBuilderPage = lazy(() =>
  import("@/features/packages/PackageBuilderPage").then((m) => ({
    default: m.PackageBuilderPage,
  })),
);

/** Event tabs that exist, by tab path. A tab without an entry shows the placeholder. */
const TAB_SCREENS: Record<string, ReactElement> = {
  brief: <BriefTab />,
  activity: <ActivityTab />,
  conversation: <ConversationTab />,
  vendors: <ShortlistTab />,
  package: <PackageBuilderPage />,
  money: <MoneyTab />,
};

function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") {
    return (
      <div className="grid min-h-screen place-items-center" role="status" aria-label="Loading">
        <span className="text-muted-foreground text-sm">Loading…</span>
      </div>
    );
  }
  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

export function AppRoutes() {
  const { roles } = useAuth();
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Suspense fallback={<Loading />}>
            <LoginPage />
          </Suspense>
        }
      />
      {/* Host pages: the token in the URL is the only credential, so they sit outside RequireAuth
          and outside the shell. They are not in APP_ROUTES either — that table drives the side
          navigation, the palette and the role guards, none of which apply to a host. */}
      <Route
        path="/accept/:token"
        element={
          <Suspense fallback={<Loading />}>
            <AcceptPage />
          </Suspense>
        }
      />
      <Route
        path="/feedback/:token"
        element={
          <Suspense fallback={<Loading />}>
            <PublicFeedbackPage />
          </Suspense>
        }
      />
      <Route
        path="/track/:token"
        element={
          <Suspense fallback={<Loading />}>
            <PublicTrackerPage />
          </Suspense>
        }
      />
      <Route
        path="/balance/:token"
        element={
          <Suspense fallback={<Loading />}>
            <PublicBalancePage />
          </Suspense>
        }
      />
      {import.meta.env.DEV && <Route path="/_components" element={<ComponentShowcase />} />}
      <Route element={<RequireAuth />}>
        <Route
          element={
            <Suspense fallback={<Loading />}>
              <AppShell />
            </Suspense>
          }
        >
          <Route index element={<Navigate to={HOME} replace />} />
          {APP_ROUTES.map((r) => (
            <Route
              key={r.path}
              path={r.path}
              element={
                canAccess(r.access, roles) ? (
                  (SCREENS[r.path] ?? <PlaceholderPage route={r} />)
                ) : (
                  <ForbiddenPage />
                )
              }
            />
          ))}
          {/* An import's preview (S33): what the agent read, checked before an event exists. */}
          <Route
            path="/events/import/:id"
            element={
              canAccess("O", roles) ? (
                <Suspense fallback={<Loading />}>
                  <ImportPreviewPage />
                </Suspense>
              ) : (
                <ForbiddenPage />
              )
            }
          />
          {/* An event's workspace: the layout holds the header and strip, the tabs fill the rest. */}
          <Route
            path="/events/:id"
            element={
              canAccess("O", roles) ? (
                <Suspense fallback={<Loading />}>
                  <EventWorkspaceLayout />
                </Suspense>
              ) : (
                <ForbiddenPage />
              )
            }
          >
            <Route index element={<Navigate to="conversation" replace />} />
            {EVENT_TABS.map((t) => (
              <Route
                key={t.path}
                path={t.path}
                element={
                  <Suspense fallback={<Loading />}>
                    {TAB_SCREENS[t.path] ?? <EventTabPlaceholder tab={t.path} />}
                  </Suspense>
                }
              />
            ))}
            {/* Not a tab: the banquet event order lives under the Package tab's own path. */}
            <Route
              path="package/beo"
              element={
                <Suspense fallback={<Loading />}>
                  <BeoPage />
                </Suspense>
              }
            />
            <Route
              path="package/preview"
              element={
                <Suspense fallback={<Loading />}>
                  <StaffPackagePreview />
                </Suspense>
              }
            />
          </Route>
          {/* One host, one vendor, one inbox thread and one configuration key: all Operator
              screens; editing is gated inside. */}
          <Route
            path="/inbox/:id"
            element={
              canAccess("O", roles) ? (
                <Suspense fallback={<Loading />}>
                  <InboxThreadPage />
                </Suspense>
              ) : (
                <ForbiddenPage />
              )
            }
          />
          <Route
            path="/hosts/:id"
            element={
              canAccess("O", roles) ? (
                <Suspense fallback={<Loading />}>
                  <HostDetailPage />
                </Suspense>
              ) : (
                <ForbiddenPage />
              )
            }
          />
          <Route
            path="/vendors/:id"
            element={
              canAccess("O", roles) ? (
                <Suspense fallback={<Loading />}>
                  <VendorDetailPage />
                </Suspense>
              ) : (
                <ForbiddenPage />
              )
            }
          />
          <Route
            path="/config/:table/:key"
            element={
              canAccess("O", roles) ? (
                <Suspense fallback={<Loading />}>
                  <ConfigKeyPage />
                </Suspense>
              ) : (
                <ForbiddenPage />
              )
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
